// Copyright (c) 2019 embyt GmbH. All rights reserved.
// Author: Roman Morawek
import { IntlState } from "view/comp/IntlProvider";
import { ILinxParameter, ICloudParameter } from "parameter";
import { IStateScheduler, INITIAL_STATE as SCHEDULER_INITIAL_STATE } from "./schedulerstates";
import { IStateGui, INITIAL_STATE as GUI_INITIAL_STATE } from "./guistates";
import { IStateSite, INITIAL_STATE as SITE_INITIAL_STATE } from "./sitestates";
import { IStateData, INITIAL_STATE as DATA_INITIAL_STATE } from "./datastates";
import { IStateTrending, INITIAL_STATE as TRENDING_INITIAL_STATE } from "./trendingstates";
import { IStateOcpp, INITIAL_STATE as OCPP_INITIAL_STATE } from "./ocppstates";
import { IStateController } from "./controllerstates";
import { IStateCached, INITIAL_STATE as CACHED_INITIAL_STATE } from "./cachedstates";
import { IStateRouter, INITIAL_STATE as ROUTER_INITIAL_STATE } from "./routerstates";

export interface IStateIdList<T> {
  [id: number]: T;
}

/*
This lists the different states of the application and their purposes.

  cached state:
    all variables which are persistent and shall remain unchanged for page reloads, even in case 
    of software updates

  router state:
    all variables directly coming from browser URL

  controller state:
    all variables only existing on webside of local engine controller
    this is not existing in cloud

  data state:
    dynamic data of controller, like energie, input status,...

  trending state:
    only time series trending data

  gui state:
    items that reflect the current status of the GUI like panel selection, input selection
    Remark: variables only used in a single component shall not be put to global state but only to local component

  ocppstate:
    similar to data state, but focused on charger data

  scheduler state:
    keeps track on AJAX requests and their last data updates

  site state:
    site list and user list

ideas for new future states:
  guipersistentstate:
    items that reflect the current status of the GUI and shall be presistent across page reloads
    (but not across software updates)

  bucket/data/container state:
    to hold answers of specifically requested items: e.g. reports, file-import-uploads,...
    typically they need significant time to generate
*/

/* top level state defintion */
export interface IState {
  intl: IntlState;
  gitVersion: string;
  parameter: ILinxParameter | ICloudParameter | undefined;

  router: IStateRouter;
  gui: IStateGui;
  cached: IStateCached;
  scheduler: IStateScheduler;
  site: IStateSite;
  data: IStateData;
  trending: IStateTrending;
  ocpp: IStateOcpp;
  controller?: IStateController;
}

/* initial state */
export const INITIAL_STATE: IState = {
  intl: {
    locale: "en",
    messages: {},
  },
  gitVersion: "<undefined>",
  parameter: undefined,

  router: ROUTER_INITIAL_STATE,
  gui: GUI_INITIAL_STATE,
  cached: CACHED_INITIAL_STATE,
  scheduler: SCHEDULER_INITIAL_STATE,
  site: SITE_INITIAL_STATE,
  data: DATA_INITIAL_STATE,
  trending: TRENDING_INITIAL_STATE,
  ocpp: OCPP_INITIAL_STATE,
};

/* define which state members shall be kept in local browser storage */
export const STORED_STATES: (keyof IState)[] = ["gitVersion", "gui", "cached"];
